import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useStudioAgentName, useWidgetConfig, customColor, useResolveColor, ConciergeHeaderWidget } from "@brandclub/bc-widgets";
import { useRef, useState, useEffect, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { getUserSignedInState, getHeadersWithUniqueVisitorId } from "../Auth/index.js";
import { fetchProductByStacklineSku } from "./api.js";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2/index.js";
import { IconCompose } from "./components/ConciergeIcons.js";
import DevOptionsPanel from "./components/DevOptionsPanel.js";
import { useDevOptions } from "./hooks/useDevOptions.js";
import { PinnedProductContext } from "./components/PinnedProductContext.js";
import { useConciergeContext } from "./ConciergeContextProvider.js";
import { useAutoScroll } from "./hooks/useAutoScroll.js";
import { useConciergeTracking } from "./hooks/useConciergeTracking.js";
import { useConversationLoader } from "./hooks/useConversationLoader.js";
import { usePageContext } from "./hooks/usePageContext.js";
import { useConciergeChat } from "./hooks/useConciergeChat.js";
import { useConciergePendingQuery } from "./stores/useConciergePendingQuery.js";
const ConciergeWidgetChatContent = ({
  pageContext: providedPageContext,
  widgetState,
  showMaximize = true,
  isMaximized = false,
  onMaximize: _onMaximize,
  onMinimize,
  onVariantSelect,
  title,
  initialQuery,
  initialQueryStacklineSku,
  onInitialQueryProcessed,
  brandId: propBrandId,
  conciergePageUrl: propConciergePageUrl
}) => {
  var _a, _b;
  const conversationState = useConciergeContext();
  const initialConversationIdRef = useRef(conversationState.conversationId);
  const initialSharedConversationIdRef = useRef(conversationState.sharedConversationId);
  const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
  const studioAgentName = useStudioAgentName();
  const params = new URLSearchParams(window.location.search);
  const brandId = (propBrandId == null ? void 0 : propBrandId.toString()) || params.get("brandId") || ((_b = (_a = config == null ? void 0 : config.brandIds) == null ? void 0 : _a[0]) == null ? void 0 : _b.toString()) || null;
  const appConfigId = params.get("appConfigId") || (config == null ? void 0 : config.appConfigId) || null;
  const [searchInputValue, setSearchInputValue] = useState("");
  const devOpts = useDevOptions();
  const [userFirstName, setUserFirstName] = useState(void 0);
  useEffect(() => {
    getUserSignedInState().then((state) => {
      var _a2, _b2;
      if (state.signedIn && ((_b2 = (_a2 = state.userInfo) == null ? void 0 : _a2.attributes) == null ? void 0 : _b2.given_name)) {
        setUserFirstName(state.userInfo.attributes.given_name);
      }
    });
  }, []);
  const getConciergePageUrl = useCallback(
    (opts = {}) => {
      const base = propConciergePageUrl ?? `${window.location.origin}/concierge`;
      const url = new URL(base);
      if (brandId) url.searchParams.set("brandId", brandId);
      if (opts.conversationId) url.searchParams.set("conversationId", opts.conversationId);
      if (opts.sharedConversationId) url.searchParams.set("sharedConversationId", opts.sharedConversationId);
      return url.toString();
    },
    [propConciergePageUrl, brandId]
  );
  const handleHistoryClick = useCallback(() => {
    window.open(getConciergePageUrl(), "_blank", "noopener,noreferrer");
  }, [getConciergePageUrl]);
  const handleMaximizeClick = useCallback(() => {
    const url = getConciergePageUrl({
      conversationId: conversationState.conversationId,
      sharedConversationId: conversationState.sharedConversationId
    });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [getConciergePageUrl, conversationState.conversationId, conversationState.sharedConversationId]);
  const cancelStreamRef = useRef(() => {
  });
  const resetChatMessagesRef = useRef(() => {
  });
  const resetConversationState = useCallback(() => {
    cancelStreamRef.current();
    resetChatMessagesRef.current();
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    conversationState.setConversationHistory([]);
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationPageContext(null);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
  }, [conversationState]);
  const handleNewConversationClick = useCallback(() => {
    resetConversationState();
  }, [resetConversationState]);
  const hookPageContext = usePageContext();
  const pageContext = providedPageContext ?? hookPageContext;
  const previousPageContextRef = useRef(null);
  const isPageContextDifferent = useCallback((a, b) => {
    if (a.pageType !== b.pageType) return true;
    if (a.pageType === "pdp" && b.pageType === "pdp" && a.stacklineSku !== b.stacklineSku) return true;
    return false;
  }, []);
  const handleStreamError = useCallback(
    (error, originalMessage) => {
      var _a2;
      setSearchInputValue(originalMessage);
      const msg = ((_a2 = error == null ? void 0 : error.message) == null ? void 0 : _a2.toLowerCase()) ?? "";
      let errorType = "unknown";
      if (msg.includes("429") || msg.includes("rate limit")) errorType = "rate_limit";
      else if (msg.includes("400") || msg.includes("bad request")) errorType = "bad_request";
      else if (msg.includes("network") || msg.includes("failed to fetch")) errorType = "network";
      else if (msg.includes("500") || msg.includes("server")) errorType = "server";
      const errorMessages = {
        rate_limit: "Too many requests. Please wait a moment before trying again.",
        bad_request: "Please enter a message before sending.",
        network: "Network error. Please check your connection and try again.",
        server: "Something went wrong on our end. Please try again.",
        unknown: "Something went wrong. Please try again."
      };
      toast.error(errorMessages[errorType]);
    },
    []
  );
  const chat = useConciergeChat({
    apiBaseUrl: conversationState.apiBaseUrl || "/api-rewards",
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    appConfigId,
    devOptions: devOpts.devOptions,
    pdpStacklineSku: (pageContext == null ? void 0 : pageContext.pageType) === "pdp" ? pageContext.stacklineSku : null,
    isOnChatWidget: true,
    onError: (error) => handleStreamError(error, searchInputValue),
    onTripwire: (message) => {
      toast.error(message);
    },
    onConversationIdGenerated: (id) => {
      conversationState.setIsActivelyStreaming(true);
      conversationState.setConversationId(id);
      conversationState.setSharedConversationId(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedAtMessageIndex(void 0);
    }
  });
  const effectiveMessages = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;
  useEffect(() => {
    const hasConversation = conversationState.conversationId != null || effectiveMessages.length > 0;
    if (hasConversation && conversationState.conversationPageContext == null) {
      conversationState.setConversationPageContext(pageContext);
    }
  }, [
    pageContext,
    conversationState.conversationId,
    effectiveMessages.length,
    conversationState.conversationPageContext,
    conversationState.setConversationPageContext
  ]);
  useEffect(() => {
    const previousContext = previousPageContextRef.current;
    const storedConversationContext = conversationState.conversationPageContext;
    if (previousContext === null) {
      previousPageContextRef.current = pageContext;
      const hasConversation = conversationState.conversationId != null || effectiveMessages.length > 0;
      if (hasConversation && storedConversationContext != null && isPageContextDifferent(pageContext, storedConversationContext)) {
        resetConversationState();
      }
      return;
    }
    const hasPageContextChanged = isPageContextDifferent(previousContext, pageContext);
    if (hasPageContextChanged) {
      resetConversationState();
    }
    previousPageContextRef.current = pageContext;
  }, [pageContext, conversationState, isPageContextDifferent, resetConversationState]);
  const pendingAccessPointRef = useRef(null);
  const isNewConversationRef = useRef(true);
  useEffect(() => {
    isNewConversationRef.current = effectiveMessages.length === 0;
  }, [effectiveMessages.length]);
  const { trackConversationStart, trackPageContext } = useConciergeTracking({
    conversationId: conversationState.conversationId,
    pageContext
  });
  const previousConversationIdRef = useRef(null);
  useEffect(() => {
    const currentId = conversationState.conversationId;
    const previousId = previousConversationIdRef.current;
    if (currentId && currentId !== previousId && pendingAccessPointRef.current) {
      const { accessPoint, query } = pendingAccessPointRef.current;
      const isNew = previousId === null;
      trackConversationStart(accessPoint, query, isNew);
      pendingAccessPointRef.current = null;
    }
    previousConversationIdRef.current = currentId;
  }, [conversationState.conversationId, trackConversationStart]);
  const handleTrackAccessPoint = useCallback(
    (accessPoint, query) => {
      pendingAccessPointRef.current = { accessPoint, query };
      if (conversationState.conversationId) {
        const isNew = isNewConversationRef.current;
        trackConversationStart(accessPoint, query, isNew);
        pendingAccessPointRef.current = null;
      }
    },
    [conversationState.conversationId, trackConversationStart]
  );
  const { isOpen } = widgetState;
  const { pendingQuery, pendingQueryId, clearPendingQuery } = useConciergePendingQuery();
  const lastProcessedQueryIdRef = useRef(null);
  useEffect(() => {
    conversationState.setIsActivelyStreaming(chat.isStreaming || chat.isLoading);
    if (chat.isStreaming) {
      const lastMsg = chat.messages[chat.messages.length - 1];
      const hasActiveParts = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" && Array.isArray(lastMsg.parts) && lastMsg.parts.some((p) => {
        var _a2;
        return p.type !== "text" || ((_a2 = p.text) == null ? void 0 : _a2.length) > 0;
      });
      if (!hasActiveParts) {
        const text = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" ? lastMsg.parts.filter((p) => p.type === "text" && "text" in p).map((p) => p.text).join("") : "";
        conversationState.setStreamingAIResponse(text);
      }
    } else if (!chat.isLoading) {
      conversationState.clearStreamingState();
    }
  }, [chat.isStreaming, chat.isLoading, chat.messages]);
  const { shouldScrollOnNextMessageRef } = useAutoScroll(
    effectiveMessages.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    effectiveMessages.length === 0
  );
  const performSearch = useCallback(
    async (text, files) => {
      shouldScrollOnNextMessageRef.current = true;
      await chat.sendMessage(text, files);
    },
    [chat, shouldScrollOnNextMessageRef]
  );
  const cancelStream = useCallback(() => {
    chat.stop();
  }, [chat]);
  const resetChatMessages = useCallback(() => {
    var _a2;
    (_a2 = chat.setMessages) == null ? void 0 : _a2.call(chat, []);
  }, [chat]);
  cancelStreamRef.current = cancelStream;
  resetChatMessagesRef.current = resetChatMessages;
  const handleSearch = useCallback(
    async (searchTerm, files) => {
      setSearchInputValue("");
      await performSearch(searchTerm, files);
    },
    [pageContext, performSearch, effectiveMessages.length]
  );
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);
  const handleStopWithRollback = useCallback(() => {
    void (async () => {
      const restoredPrompt = await chat.cancelLatestTurn();
      conversationState.setIsActivelyStreaming(false);
      conversationState.clearStreamingState();
      if (restoredPrompt) {
        setSearchInputValue(restoredPrompt);
      }
    })();
  }, [chat, conversationState]);
  const handleUpdateFeedback = (messageIndex, feedback) => {
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || void 0
          };
        }
        return msg;
      });
    });
  };
  const { isLoadingConversationQuery } = useConversationLoader({
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: effectiveMessages.length,
    apiBaseUrl: conversationState.apiBaseUrl,
    onConversationLoaded: (messages, followUpQuestions, sharingInfo) => {
      var _a2;
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      (_a2 = chat.setMessages) == null ? void 0 : _a2.call(chat, messages);
      if (sharingInfo) {
        conversationState.setIsSharedView(sharingInfo.isSharedView ?? false);
        conversationState.setSharedConversationId(sharingInfo.sharedConversationId || null);
        conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
        conversationState.setSharedAtMessageIndex(sharingInfo.sharedAtMessageIndex);
        conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
        conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
      } else {
        conversationState.setSharedConversationId(null);
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(void 0);
        conversationState.setSharedAtMessageIndex(void 0);
        conversationState.setSourceConversationId(void 0);
        conversationState.setSourceCheckpointIndex(void 0);
      }
    },
    onConversationNotFound: () => {
      const isInitialUrlCleanup = conversationState.conversationId === initialConversationIdRef.current && conversationState.sharedConversationId === initialSharedConversationIdRef.current;
      if (!isInitialUrlCleanup) {
        toast.error("Conversation not found.");
      }
      conversationState.setConversationId(null);
      conversationState.setSharedConversationId(null);
      conversationState.setConversationPageContext(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedByUserName(void 0);
      conversationState.setSharedAtMessageIndex(void 0);
      conversationState.setSourceConversationId(void 0);
      conversationState.setSourceCheckpointIndex(void 0);
    }
  });
  useEffect(() => {
    if (!pendingQuery || !pendingQueryId || !isOpen) {
      return;
    }
    if (!performSearch || typeof performSearch !== "function") {
      return;
    }
    if (lastProcessedQueryIdRef.current === pendingQueryId) {
      return;
    }
    lastProcessedQueryIdRef.current = pendingQueryId;
    const queryToSend = pendingQuery;
    clearPendingQuery();
    const messageToSend = queryToSend;
    const timeoutId = setTimeout(() => {
      if (!isOpen || !performSearch || typeof performSearch !== "function") {
        lastProcessedQueryIdRef.current = null;
        return;
      }
      try {
        handleTrackAccessPoint("conversation_starter", queryToSend);
        performSearch(messageToSend);
      } catch (error) {
        console.error("Error processing pending query:", error);
        toast.error("Failed to process search. Please try again.");
        lastProcessedQueryIdRef.current = null;
      }
    }, 100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    pendingQuery,
    pendingQueryId,
    performSearch,
    clearPendingQuery,
    handleTrackAccessPoint
  ]);
  const prevIsSignedInRef = useRef(void 0);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    prevIsSignedInRef.current = conversationState.isSignedIn;
    if (wasSignedIn !== false || !conversationState.isSignedIn) return;
    setTimeout(() => handleSearchRef.current("I just signed in."), 300);
  }, [conversationState.isSignedIn]);
  const lastProcessedInitialQueryRef = useRef(null);
  const isProcessingInitialQueryRef = useRef(false);
  useEffect(() => {
    if (!initialQuery || !isOpen) {
      return;
    }
    if (!performSearch || typeof performSearch !== "function") {
      return;
    }
    if (lastProcessedInitialQueryRef.current === initialQuery) {
      return;
    }
    if (isProcessingInitialQueryRef.current) {
      return;
    }
    isProcessingInitialQueryRef.current = true;
    const queryToSend = initialQuery;
    const messageToSend = queryToSend;
    const timeoutId = setTimeout(() => {
      if (!isOpen || !performSearch || typeof performSearch !== "function") {
        isProcessingInitialQueryRef.current = false;
        return;
      }
      try {
        handleTrackAccessPoint("conversation_starter", queryToSend);
        performSearch(messageToSend);
        lastProcessedInitialQueryRef.current = queryToSend;
        onInitialQueryProcessed == null ? void 0 : onInitialQueryProcessed();
      } catch (error) {
        console.error("Error processing initial query:", error);
        toast.error("Failed to process search. Please try again.");
      } finally {
        isProcessingInitialQueryRef.current = false;
      }
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      isProcessingInitialQueryRef.current = false;
    };
  }, [isOpen, initialQuery, performSearch, onInitialQueryProcessed, handleTrackAccessPoint]);
  const [pinnedProduct, setPinnedProduct] = useState(null);
  const [isPinnedProductLoading, setIsPinnedProductLoading] = useState(false);
  useEffect(() => {
    const currentPageType = pageContext == null ? void 0 : pageContext.pageType;
    const currentStacklineSku = currentPageType === "pdp" ? pageContext == null ? void 0 : pageContext.stacklineSku : void 0;
    setPinnedProduct(null);
    setIsPinnedProductLoading(false);
    if (!currentStacklineSku) {
      return;
    }
    let isCancelled = false;
    const fetchProduct = async () => {
      setIsPinnedProductLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const product = await fetchProductByStacklineSku({
          stacklineSku: currentStacklineSku,
          headersToInject: headers,
          apiBaseUrl: conversationState.apiBaseUrl
        });
        if (!isCancelled && product) {
          setPinnedProduct(product);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching pinned product:", error);
        }
      } finally {
        if (!isCancelled) {
          setIsPinnedProductLoading(false);
        }
      }
    };
    fetchProduct();
    return () => {
      isCancelled = true;
    };
  }, [pageContext == null ? void 0 : pageContext.pageType, pageContext == null ? void 0 : pageContext.stacklineSku, conversationState.apiBaseUrl]);
  const filteredConversationHistory = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;
  const filteredStreamingAIResponse = conversationState.streamingAIResponse;
  const panelState = useWidgetConfig("concierge-panel", {
    preset: "vertical-expanded",
    mainBgColor: customColor("transparent"),
    pillLayout: "vertical",
    pillShowIcon: false,
    pillBorderRadius: 100,
    pillBorderColor: customColor("#F3F3F3"),
    pillBorderWidth: 1,
    pillShadowEnabled: true,
    pillBgColor: customColor("#ffffff"),
    pillTextColor: customColor("#181d56"),
    pillShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)",
    pillContainerBgColor: customColor("transparent"),
    pillContainerBorderRadius: 12,
    inputBorderColor: customColor("#F3F3F3"),
    inputBorderWidth: 1,
    inputBorderRadius: 20,
    inputMinHeight: 116,
    inputShadowEnabled: false,
    inputShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)"
  });
  const resolveColor = useResolveColor();
  const mainBg = resolveColor(panelState.mainBgColor);
  const hasMainBg = mainBg && mainBg !== "transparent" && mainBg !== "";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Toaster, {}),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-root",
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          maxHeight: "100%",
          overflow: "hidden",
          backgroundColor: hasMainBg ? mainBg : void 0
        },
        children: [
          /* @__PURE__ */ jsx(
            ConciergeHeaderWidget,
            {
              showMaximize,
              isMaximized,
              onMaximize: handleMaximizeClick,
              onMinimize,
              onClose: widgetState.close,
              onHistoryClick: handleHistoryClick,
              onNewConversationClick: handleNewConversationClick,
              showNewConversation: true,
              newConversationIcon: /* @__PURE__ */ jsx(IconCompose, { size: 16 }),
              title: title || studioAgentName || (config == null ? void 0 : config.conciergeAgentName),
              bottomContent: /* @__PURE__ */ jsx(
                PinnedProductContext,
                {
                  pageContext,
                  product: pinnedProduct,
                  isLoading: isPinnedProductLoading
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(DevOptionsPanel, { ...devOpts }),
          /* @__PURE__ */ jsx(
            ConciergeChatAreaV2,
            {
              appName: config == null ? void 0 : config.appName,
              conciergeName: studioAgentName || (config == null ? void 0 : config.conciergeAgentName),
              brandId,
              conversationId: conversationState.conversationId,
              isEmbedded: true,
              isOnChatWidget: true,
              apiBaseUrl: conversationState.apiBaseUrl,
              userFirstName,
              pageContext,
              conversationHistory: filteredConversationHistory,
              followUpQuestions: conversationState.followUpQuestions,
              latestFollowUps: chat.latestFollowUps,
              isLoadingFollowUps: chat.isLoadingFollowUps,
              streamingStoreLocator: conversationState.streamingStoreLocator,
              isActivelyStreaming: conversationState.isActivelyStreaming,
              transientStatus: chat.transientStatus,
              streamingAIResponse: filteredStreamingAIResponse,
              isLoadingConversationQuery,
              handleSearch,
              onRetryLastMessage: chat.retryLastMessage,
              onUpdateFeedback: handleUpdateFeedback,
              onVariantSelect,
              onTrackAccessPoint: handleTrackAccessPoint,
              onStop: handleStopWithRollback,
              searchInputValue,
              setSearchInputValue,
              isSharedView: conversationState.isSharedView,
              sharedByUserName: conversationState.sharedByUserName,
              sourceConversationId: conversationState.sourceConversationId,
              sourceCheckpointIndex: conversationState.sourceCheckpointIndex
            }
          )
        ]
      }
    )
  ] });
};
const ConciergeWidgetChat = (props) => {
  return /* @__PURE__ */ jsx(ConciergeWidgetChatContent, { ...props });
};
export {
  ConciergeWidgetChat
};
//# sourceMappingURL=ConciergeWidgetChat.js.map
